import { useEffect, useState } from 'react';
import { useFetchFromServer } from 'core/ui/utils/useFetchFromServer';
import { useFetchObjectFromServer } from 'core/ui/utils/useFetchObjectFromServer';
import {
  postIntegration,
  patchIntegration,
  patchConfig,
  getIntegrations,
  getEnrichments,
  deleteIntegration,
} from '../utils/fetchFromServer';
import {
  Integration,
  IntegrationResponse,
  IntegrationName,
  IntegrationType,
  SESGoConfig,
} from '../context/IntegrationsState';
import { EntityWithId } from 'services/genericAPIGatewayService';

/** Path segment for dispatcher-config-api: `/sites/{ownerId}/configs/...` (not legacy `/dispatchers/`). */
const DISPATCHER_CONFIGS_PATH_SEGMENT = 'configs';

const generateUrl = (siteId: string, integrationType: string, integrationName?: string): string => {
  const resourceSegment =
    integrationType === IntegrationType.DISPATCHERS ? DISPATCHER_CONFIGS_PATH_SEGMENT : integrationType;
  const baseUrl = `/sites/${siteId}/${resourceSegment}`;

  if (integrationName) {
    return integrationType === 'enrichments'
      ? `${baseUrl}/settings/${integrationName}`
      : `${baseUrl}/${integrationName}`;
  }

  return integrationType === 'enrichments' ? `${baseUrl}/settings` : baseUrl;
};

/** New dispatcher-config-api nests ids and metadata under `base`; older services used a flat body. */
type DispatcherConfigApiNested = {
  base?: {
    id?: string;
    ownerId?: string;
    channel?: IntegrationResponse['channel'];
    dispatcher?: IntegrationName;
  };
  config?: IntegrationResponse['config'];
};

/** Raw dispatcher row from `/sites/{siteId}/configs` before `normalizeDispatcherIntegrationResponse`. */
export type DispatcherIntegrationApiRow = IntegrationResponse | DispatcherConfigApiNested;

export function normalizeDispatcherIntegrationResponse(
  raw: DispatcherIntegrationApiRow | null | undefined,
): IntegrationResponse | null {
  if (raw == null) {
    return null;
  }
  const r = raw as IntegrationResponse & DispatcherConfigApiNested;
  if (r.base != null && typeof r.base === 'object') {
    return {
      id: (r.base.id ?? r.id) as string,
      ownerId: r.base.ownerId ?? r.ownerId,
      dispatcher: (r.base.dispatcher ?? r.dispatcher) as IntegrationName,
      channel: r.base.channel ?? r.channel,
      config: (r.config ?? {}) as IntegrationResponse['config'],
    };
  }
  return r as IntegrationResponse;
}

const transformDispatcherIntegration = (integration: IntegrationResponse | null): Integration => {
  if (!integration) {
    return null;
  }
  return {
    config: {
      ...integration.config,
    },
    enrichment: integration.dispatcher,
    id: integration.id,
    version: 1,
    channel: integration.channel,
  };
};

const transformSESGoConfig = ({ configSet, ...config }: SESGoConfig) => {
  const updatedConfig: SESGoConfig = { ...config };
  return updatedConfig;
};

export async function createIntegration(
  siteId: string,
  integrationType: IntegrationType,
  integrationName: IntegrationName,
  integration: Integration,
) {
  const intName = integrationType === 'dispatchers' ? integration.enrichment : '';
  const url = generateUrl(siteId, integrationType, intName);

  let transformedIntegration: Integration;
  switch (integrationName) {
    case IntegrationName.SES:
      transformedIntegration = { ...integration, config: { ...transformSESGoConfig(integration.config) } };
      break;
    case IntegrationName.STUB:
    case IntegrationName.AMADEUS:
    case IntegrationName.VOUCHER_CODES:
      transformedIntegration = { ...integration, config: { ...integration.config } };
      break;
    default:
      transformedIntegration = { ...integration };
  }
  return await postIntegration(url, transformedIntegration);
}

export async function updateIntegration(
  siteId: string,
  configId: string,
  integrationType: IntegrationType,
  integrationName: IntegrationName,
  integration: Integration,
) {
  const url = `${generateUrl(siteId, integrationType, integration.enrichment)}${
    integrationType === IntegrationType.DISPATCHERS ? `/${configId}` : ''
  }`;

  switch (integrationName) {
    case IntegrationName.SES:
      const sesGoConfig: SESGoConfig = { ...transformSESGoConfig(integration.config) };
      return await patchConfig(url, sesGoConfig);
    case IntegrationName.LINKMOBILITYSMS:
    case IntegrationName.LINKMOBILITYRCS:
    case IntegrationName.WAX:
    case IntegrationName.AMADEUS:
    case IntegrationName.REVIEWS:
      return await patchConfig(url, integration.config);
    case IntegrationName.THIRD_PARTY_CONSENT:
      return await patchIntegration(generateUrl(siteId, integrationType), integration);
    default:
      return await patchIntegration(url, integration);
  }
}

export async function getDispatcherByChannel(siteId: string, channelName: string) {
  const url = `/sites/${siteId}/${DISPATCHER_CONFIGS_PATH_SEGMENT}/channel/${channelName}`;
  try {
    const result = await getIntegrations(url);
    const normalized = normalizeDispatcherIntegrationResponse(result.entity as unknown);
    if (normalized == null) {
      return {} as EntityWithId<Integration>;
    }
    return {
      id: result.id ?? normalized.id,
      entity: transformDispatcherIntegration(normalized),
    } as EntityWithId<Integration>;
  } catch {
    return {} as EntityWithId<Integration>;
  }
}

export async function getEnrichmentByType(siteId: string, type: string) {
  const url = generateUrl(siteId, 'enrichments');
  try {
    const enrichments = await getEnrichments(url);
    return enrichments.find(x => x.entity.enrichment === type);
  } catch {
    return {} as EntityWithId<Integration>;
  }
}

export async function deleteIntegrationById(
  siteId: string,
  configId: string,
  integrationType: IntegrationType,
  integrationName: IntegrationName,
) {
  const url = `${generateUrl(siteId, integrationType, integrationName)}${
    integrationType === IntegrationType.DISPATCHERS ? `/${configId}` : ''
  }`;

  return await deleteIntegration(url);
}

/** Fetches all of the enrichments for a specific siteId */
export const useEnrichments = (siteId: string, integrationsEnabled: boolean) => {
  const [url, setUrl] = useState('');
  const [enrichments, setEnrichments] = useState<Integration[]>([]);

  const {
    state: { data, status },
    setUrl: fetchOptions,
  } = useFetchFromServer<Integration>();

  useEffect(() => {
    // When the siteId changes we need to reset the data
    setEnrichments([]);

    // Trigger the fetch for the new siteId
    setUrl(generateUrl(siteId, 'enrichments'));
  }, [siteId]);

  useEffect(() => {
    if (integrationsEnabled && url !== '') {
      fetchOptions(url);
    }
  }, [integrationsEnabled, fetchOptions, url]);

  useEffect(() => {
    setEnrichments(data);
  }, [data]);

  return { enrichments, status: integrationsEnabled ? status : 'resolved' };
};

/** Fetches all of the dispatchers for a specific siteId */
export const useDispatchers = (siteId: string, integrationsEnabled: boolean) => {
  const [url, setUrl] = useState('');
  const [dispatchers, setDispatchers] = useState<IntegrationResponse[]>([]);

  const {
    state: { data, status },
    setUrl: fetchOptions,
  } = useFetchFromServer<IntegrationResponse>();

  useEffect(() => {
    // When the siteId changes we need to reset the data
    setDispatchers([]);

    // Trigger the fetch for the new siteId
    setUrl(generateUrl(siteId, IntegrationType.DISPATCHERS));
  }, [siteId]);

  useEffect(() => {
    if (integrationsEnabled && url !== '') {
      fetchOptions(url);
    }
  }, [integrationsEnabled, fetchOptions, url]);

  useEffect(() => {
    if (!Array.isArray(data)) {
      setDispatchers([]);
      return;
    }
    setDispatchers(
      data
        .map(row => normalizeDispatcherIntegrationResponse(row as unknown))
        .filter((row): row is IntegrationResponse => row != null),
    );
  }, [data]);

  return { dispatchers, status: integrationsEnabled ? status : 'resolved' };
};

/** Fetches the third party consent for a specific siteId */
export const useThirdPartyConsent = (siteId: string, integrationsEnabled: boolean) => {
  const [url, setUrl] = useState('');
  const [thirdPartyConsent, setThirdPartyConsent] = useState<Integration>();

  const {
    state: { data, status },
    setUrl: fetchOptions,
  } = useFetchObjectFromServer<Integration>();

  useEffect(() => {
    // When the siteId changes we need to reset the data
    setThirdPartyConsent(null);

    // Trigger the fetch for the new siteId
    setUrl(generateUrl(siteId, IntegrationType.THIRD_PARTY_CONSENT));
  }, [siteId]);

  useEffect(() => {
    if (integrationsEnabled && url !== '') {
      fetchOptions(url);
    }
  }, [integrationsEnabled, fetchOptions, url]);

  useEffect(() => {
    setThirdPartyConsent(data?.entity);
  }, [data]);

  return { thirdPartyConsent, status: integrationsEnabled ? status : 'resolved' };
};

/** Fetches a list of integration options for the enrichments or dispatchers */
export const useIntegrationOptions = (integrationsEnabled: boolean, initialType = '') => {
  const [type, setType] = useState(initialType);
  const [url, setUrl] = useState('');

  const {
    state: { data, status },
    setUrl: fetchOptions,
  } = useFetchFromServer<string>();

  useEffect(() => {
    if (type !== '') {
      setUrl('/' + type);
    }
  }, [type]);

  useEffect(() => {
    if (integrationsEnabled && url !== '') {
      fetchOptions(url);
    }
  }, [integrationsEnabled, fetchOptions, url]);

  return { data, status: integrationsEnabled ? status : 'resolved', setType };
};

const capitalizeFirstLetter = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);

/** Fetches the details for an integration */
export const useIntegration = (
  integrationsEnabled: boolean,
  initialSiteId = '',
  initialId = '',
  initialType = '',
  initialName = '',
) => {
  const [integration, setIntegration] = useState<Integration>(null);
  const [siteId, setSiteId] = useState(initialSiteId);
  const [integrationId, setIntegrationId] = useState(initialId);
  const [integrationType, setIntegrationType] = useState(initialType);
  const [integrationName, setIntegrationName] = useState(initialName);
  const [url, setUrl] = useState('');

  const {
    state: { data, status },
    setUrl: fetchOptions,
  } = useFetchObjectFromServer<Integration>();

  useEffect(() => {
    if (integrationsEnabled && siteId && integrationName) {
      const intName =
        integrationType === IntegrationType.DISPATCHERS
          ? integrationName
          : integrationType !== IntegrationType.THIRD_PARTY_CONSENT
          ? capitalizeFirstLetter(integrationName)
          : '';
      setUrl(
        `${generateUrl(siteId, integrationType, intName)}${
          integrationType === IntegrationType.DISPATCHERS ? `/${integrationId}` : ''
        }`,
      );
    }
  }, [integrationsEnabled, siteId, integrationId, integrationType, integrationName]);

  useEffect(() => {
    if (integrationsEnabled && url) fetchOptions(url);
  }, [integrationsEnabled, fetchOptions, url]);

  useEffect(() => {
    const integrationData =
      integrationName === IntegrationName.STUB ||
      integrationName === IntegrationName.SES ||
      integrationName === IntegrationName.LINKMOBILITYSMS ||
      integrationName === IntegrationName.LINKMOBILITYRCS ||
      integrationName === IntegrationName.WAX
        ? transformDispatcherIntegration(normalizeDispatcherIntegrationResponse(data.entity as unknown))
        : data.entity;

    setIntegration(integrationData);
  }, [data, integrationName]);

  return {
    integration,
    status: integrationsEnabled ? status : 'resolved',
    setIntegrationId,
    setIntegrationName,
    setIntegrationType,
    setSiteId,
  };
};
